// Shared queries for public catalog vocabularies.
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './client'
import { qk } from './queries'
import type { MarketplaceListing, MarketplaceResponse, ToolCategoriesResponse, ToolCategory } from './types'

/** GET /tool-categories (public). Display order is `sort_order`. */
export function useToolCategories() {
  const q = useQuery({
    queryKey: qk.toolCategories,
    queryFn: () => apiFetch<ToolCategoriesResponse>('/tool-categories'),
    staleTime: 10 * 60_000,
    retry: false,
  })
  const categories: ToolCategory[] = [...(q.data?.categories ?? [])].sort((a, b) => a.sort_order - b.sort_order)
  return { ...q, categories }
}

/** GET /marketplace's page_size ceiling. */
export const MARKETPLACE_PAGE_SIZE = 100
/** Safety stop so a gateway that misreports `total` can't spin forever. */
const MARKETPLACE_MAX_PAGES = 100

/**
 * Every marketplace listing: GET /marketplace is page-based (`page`, `page_size` ≤ 100;
 * `limit` is ignored), so request full pages until `page * page_size >= total` or a
 * page comes back short.
 */
export async function fetchAllMarketplaceListings(): Promise<MarketplaceListing[]> {
  const all: MarketplaceListing[] = []
  for (let page = 1; page <= MARKETPLACE_MAX_PAGES; page++) {
    const r = await apiFetch<MarketplaceResponse>(`/marketplace?page=${page}&page_size=${MARKETPLACE_PAGE_SIZE}`)
    const items = r.items ?? []
    all.push(...items)
    const size = r.page_size || MARKETPLACE_PAGE_SIZE
    if (items.length < size || page * size >= (r.total ?? 0)) break
  }
  return all
}
